/**
 * Helpers specific to the Potential Licensing page. Kept separate from
 * lib/format.js because nothing here applies to the clearance search —
 * "which classes overlap with what I searched" and "where might I reach
 * this owner" are licensing-only questions.
 */

/** Nice class numbers the person actually searched for, as plain strings ("25", not "025"). */
export function parseSearchedClasses(query) {
  if (!query?.niceClasses) return [];
  const raw = Array.isArray(query.niceClasses)
    ? query.niceClasses
    : String(query.niceClasses).split(",");
  return raw.map((c) => String(c).trim().replace(/^0+/, "")).filter(Boolean);
}

/**
 * Splits a result's goods & services into what overlaps with the searched
 * classes (the actual reason this mark is a licensing candidate) versus
 * everything else it also covers. With no classes in the search there's
 * nothing to compare against, so everything is "other" rather than
 * guessing at a match.
 */
export function splitGoodsAndServices(goodsAndServices, searchedClasses) {
  const list = goodsAndServices || [];
  if (!searchedClasses.length) return { matched: [], other: list };

  const wanted = new Set(searchedClasses);
  const matched = [];
  const other = [];
  for (const item of list) {
    const num = String(item.classNumber || "").replace(/^0+/, "");
    (wanted.has(num) ? matched : other).push(item);
  }
  return { matched, other };
}

/**
 * The register has no email, phone or website for an owner — public
 * trademark registers never publish that, only a legal correspondence
 * address. These are outreach *shortcuts* (a prebuilt search), not
 * verified contact details, and must stay labelled as such wherever
 * they're shown.
 */

export function contactSearchLinks(trademarkName) {
  const name = (trademarkName || "").trim();
  if (!name) return null;
  const q = encodeURIComponent(name);
  return {
    web: `https://www.google.com/search?q=${q}+official+website`,
    linkedin: `https://www.linkedin.com/search/results/companies/?keywords=${q}`,
  };
}
